import { EditConflict } from '../components/EditConflict';
/**
 * 티켓 상세의 **내용**. 껍데기는 둘이 각자 쓴다 —
 * **모달(`TicketDialog`) 하나가 쓴다.** 옛 페이지(`TicketPage`)는
 * 2026-08-29 에 걷었다 — 티켓 상세는 무조건 모달이다(지시).
 *
 * **고칠 곳을 두 번 누르면 열린다**(2026-08-29 지시로 「수정」 단추를 걷었다).
 * 열리고 나면 속성은 고르는 즉시 저장되고, **제목과 설명만** 아래 고정 바의
 * 「저장」을 기다린다 — 글자를 치는 동안 매번 보내면 요청이 폭주하고
 * 되돌리기가 사라진다.
 *
 * **바뀐 카드를 `onChanged` 로 올려보낸다.** 모달에서 담당자를 바꾸고 닫았을
 * 때 뒤의 보드가 옛 값을 쥐고 있으면, 사람은 저장이 안 된 것으로 읽는다.
 */

import { useEffect, useState, type ClipboardEvent } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import {
  ApiError,
  can,
  deleteTicket,
  errorMessage,
  getTicket,
  holdTicket,
  listLabels,
  listMembers,
  moveTicket,
  NotLoggedInError,
  updateTicket,
  type Label,
  type Project,
  type TicketCard,
  type TicketCol,
  type TicketDetail as TicketDetailData,
  type User,
  type UserBrief,
  ROLE_LABEL,
} from '../api';
import { imageFilesFromDataTransfer, uploadAndInsert } from '../attachments';
import {
  AssigneeMark,
  BADGE,
  DueMark,
  LabelChip,
  PriorityMark,
  toneVars,
  TypeMark,
} from '../board/Chips';
import {
  columnLabel,
  COLUMNS,
  deviceLabel,
  environmentLabel,
  typeOf,
  DEVICES,
  ENVIRONMENTS,
  kindLabel,
  PRIORITIES,
  priorityOf,
  TYPES,
  workSpan,
} from '../columns';
import { Check, ChoiceGroup, Radio } from '../ui/Choice';
import { Markdown } from '../ui/Markdown';
import { Activity } from './Activity';
import { Comments } from './Comments';
import { copyText, ticketUrl } from './link';
import { SectionTitle } from './section';

import { BodyHistory } from './BodyHistory';

export function TicketDetail({
  ticketId,
  user,
  projects,
  onError,
  onUnauthorized,
  onClose,
  onChanged,
  onDeleted,
  onDirtyChange,
}: {
  ticketId: number;
  user: User;
  /** 옮길 수 있는 프로젝트. 하나뿐이면 고를 것이 없다. */
  projects: Project[];
  onError: (message: string | null) => void;
  onUnauthorized: () => void;
  onClose: () => void;
  onChanged?: (card: TicketCard) => void;
  onDeleted?: (id: number) => void;
  /** 안 저장한 제목·설명이 있나. 모달이 닫기 전에 묻는 데 쓴다. */
  onDirtyChange?: (dirty: boolean) => void;
}) {
  const [ticket, setTicket] = useState<TicketDetailData | null>(null);
  const [members, setMembers] = useState<UserBrief[]>([]);
  const [labels, setLabels] = useState<Label[]>([]);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [textVersion, setTextVersion] = useState<string | undefined>();
  const [conflict, setConflict] = useState<TicketDetailData | null>(null);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  /**
   * 고치는 중인가(2026-08-26 지시).
   *
   * 옛 판은 **늘 편집 상태**였다 — 열자마자 라디오가 펼쳐져 있고 누르면 바로
   * 저장됐다. 티켓을 **읽으러** 연 사람에게는 그것이 위험하다. 잘못 스치면
   * 값이 바뀌고 되돌릴 자리가 없다.
   *
   * 이제 기본은 읽기다. **고칠 곳을 두 번 눌러야** 손잡이가 나온다.
   */
  const [editing, setEditing] = useState(false);
  /**
   * 붙여넣은 파일을 올리는 중인가. 실패 문구도 이 옆에 선다.
   *
   * **만들 때는 되고 고칠 때는 안 됐다** — 2026-09-01 QA 역할 검증이 짚은
   * 자리다. 서버는 `ticket.edit` 만 있으면 이미 통과시키고 있었고, 화면이
   * 그 길을 안 냈을 뿐이다. 그래서 스크린샷이 텔레그램에 남았다.
   */
  const [pasting, setPasting] = useState(false);
  const [pasteError, setPasteError] = useState<string | null>(null);
  /**
   * 보류 사유 초안(2026-09-02). **한 자씩 안 보낸다** — 옆 저장 단추를
   * 눌러야 나간다. 소요 일수(`EstimateInput`)와 같은 이유다.
   */
  const [holdReason, setHoldReason] = useState('');
  /** 보류 요청이 도는 중. 단추 두 번 눌림을 막는다. */
  const [holding, setHolding] = useState(false);
  /**
   * 되돌리기를 **한 번 더 묻는 중인가.**
   *
   * 되돌리면 서버가 사유도 함께 지우는데(`hold_ticket`), 그것은 되돌릴 길이
   * 없다 — 적어 둔 「왜 안 하나」가 사라진다. 사유가 없으면 잃을 것이
   * 없으므로 안 묻는다.
   */
  const [holdConfirm, setHoldConfirm] = useState(false);

  const badId = !Number.isInteger(ticketId) || ticketId <= 0;

  useEffect(() => {
    if (badId) return;
    let cancelled = false;
    void (async () => {
      try {
        const data = await getTicket(ticketId);
        if (cancelled) return;
        setTicket(data);
        setTitle(data.title);
        setBody(data.body);
        setHoldReason(data.hold_reason ?? '');
        const [labelData, memberData] = await Promise.all([
          listLabels(data.project_id),
          listMembers(),
        ]);
        if (cancelled) return;
        setLabels(labelData.labels);
        setMembers(memberData.members);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof NotLoggedInError) {
          onUnauthorized();
          return;
        }
        setFailed(true);
        onError(`티켓을 불러오지 못했다: ${errorMessage(err)}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ticketId, badId, onError, onUnauthorized]);

  /**
   * 서버가 돌려준 카드를 받아들인다.
   *
   * **편집 중인 판(`textVersion`)은 내가 성공시킨 쓰기로만 민다.** `base` 는
   * 그 쓰기가 바탕으로 삼은 판이다 — 바탕이 내 판과 같을 때만 새 판을
   * 잇는다. 코드 연결·QA 충돌 같은 **외부 재조회**로 `ticket.updated_at` 이
   * 앞서 갔으면 바탕이 어긋나므로 판을 안 밀고, 제목·설명 저장은 그 옛
   * 판을 보내 서버의 409 에 걸린다 — 남의 변경을 조용히 덮지 않는다.
   */
  function adopt(saved: TicketCard, base: string) {
    setTicket((prev) => (prev ? { ...prev, ...saved } : prev));
    setTextVersion((v) => (v !== undefined && v === base ? saved.updated_at : v));
  }

  async function patch(input: Parameters<typeof updateTicket>[1]) {
    if (!ticket) return;
    setSaving(true);
    onError(null);
    // 편집 중이면 **편집 판**을 보낸다. 라디오를 누를 때마다 `updated_at` 이
    // 바뀌는데 제목 저장이 열 때의 판을 보내면 자기 변경에 409 가 났다.
    const base = textVersion ?? ticket.updated_at;
    try {
      const saved = await updateTicket(ticket.id, { expected_updated_at: base, ...input });
      adopt(saved, base);
      if (input.body !== undefined) setTicket((prev) => (prev ? { ...prev, body: input.body as string } : prev));
      onChanged?.(saved);
      // **제목·설명만 알린다.** 나머지 속성은 고르는 즉시 그 자리가 바뀌어
      // 보이므로, 토스트까지 띄우면 고를 때마다 오른쪽 위가 시끄럽다.
      if (input.title !== undefined) toast.success('제목을 저장했다');
      if (input.body !== undefined) toast.success('설명을 저장했다');
      return true;
    } catch (err) {
      if (err instanceof NotLoggedInError) {
        onUnauthorized();
        return;
      }
      if (err instanceof ApiError && err.status === 409) {
        try { setConflict(await getTicket(ticket.id)); }
        catch (loadError) { onError(errorMessage(loadError)); }
      }
      onError(`고치지 못했다: ${errorMessage(err)}`);
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function move(col: TicketCol, estimateDays?: number | null) {
    if (!ticket) return;
    setSaving(true);
    onError(null);
    try {
      const base = textVersion ?? ticket.updated_at;
      const saved = await moveTicket(ticket.id, {
        expected_updated_at: base,
        col,
        ...(estimateDays !== undefined ? { estimate_days: estimateDays } : {}),
      });
      adopt(saved, base);
      onChanged?.(saved);
      // 칸이 바뀌는 것은 큰 변화다 — 일자가 찍히거나 지워진다.
      // 소요 일수만 고친 경우(칸이 그대로)는 조용히 둔다.
      if (ticket.col !== col) toast.success(`${saved.key} → ${columnLabel(col)}`);
      else if (estimateDays !== undefined) toast.success('소요 예정을 저장했다');
    } catch (err) {
      if (err instanceof NotLoggedInError) {
        onUnauthorized();
        return;
      }
      onError(`칸을 옮기지 못했다: ${errorMessage(err)}`);
    } finally {
      setSaving(false);
    }
  }

  /**
   * 미뤄 두거나 되돌린다(2026-09-02 사용자 확정).
   *
   * ⚠ **칸을 안 건드린다.** 보류는 칸과 다른 축이라 어느 칸에 있든 미룰 수
   * 있고, 미뤄도 그 칸에 그대로 있다. 그래서 `move` 가 아니라 따로 있다.
   *
   * 사유는 **선택**이다. 적으라고 강요하면 미루는 것 자체를 안 하게 되고,
   * 그러면 안 할 요청이 이슈 칸에 그대로 남는다 — 이 기능을 만든 이유가
   * 사라진다. 그래서 「미루기」는 사유 없이 한 번에 나가고, 사유는 미뤄 둔
   * 뒤에 덧붙인다. 서버가 **이미 미룬 것을 다시 미루면 시각을 안 바꾸므로**
   * 그 왕복이 「언제 미뤘나」를 밀지 않는다.
   */
  async function hold(held: boolean, reason?: string | null) {
    if (!ticket || holding) return;
    setHolding(true);
    onError(null);
    try {
      const base = textVersion ?? ticket.updated_at;
      const saved = await holdTicket(ticket.id, held, reason, base);
      adopt(saved, base);
      onChanged?.(saved);
      // 서버가 다듬은 값으로 초안을 맞춘다 — 빈 문자열은 서버에서 `null` 이
      // 되므로, 안 맞추면 저장 단추가 계속 켜져 있다.
      setHoldReason(saved.hold_reason ?? '');
      setHoldConfirm(false);
      if (!held) toast.success('보류를 되돌렸다');
      else if (reason === undefined) toast.success('미뤄 뒀다');
      else toast.success('보류 사유를 저장했다');
    } catch (err) {
      if (err instanceof NotLoggedInError) {
        onUnauthorized();
        return;
      }
      onError(`보류를 바꾸지 못했다: ${errorMessage(err)}`);
    } finally {
      setHolding(false);
    }
  }

  async function remove() {
    if (!ticket) return;
    setSaving(true);
    try {
      await deleteTicket(ticket.id);
      onDeleted?.(ticket.id);
      onClose();
    } catch (err) {
      setSaving(false);
      if (err instanceof NotLoggedInError) {
        onUnauthorized();
        return;
      }
      onError(`지우지 못했다: ${errorMessage(err)}`);
    }
  }

  /* 안 저장한 글이 있는지 모달에 알린다. **이른 return 보다 앞이어야 한다** —
     훅 순서가 티켓을 읽기 전후로 달라지면 안 된다. */
  const unsaved = ticket !== null && (title.trim() !== ticket.title || body !== ticket.body);
  useEffect(() => {
    onDirtyChange?.(unsaved);
  }, [unsaved, onDirtyChange]);

  // ⚠ **읽는 동안에도 여백이 필요하다.** 모달이 `p-0` 이라(세 층 틀)
  // 그냥 글자만 두면 테두리에 붙는다.
  if (badId || failed) {
    return <p className="page-status p-s6">없는 티켓이다.</p>;
  }
  if (!ticket) return <p className="page-status p-s6">티켓을 읽는 중…</p>;

  const titleDirty = title.trim() !== ticket.title;
  const bodyDirty = body !== ticket.body;

  const canEdit = can(user, 'ticket.edit');

  /**
   * 머리 막대와 본문 인용선의 색. **우선순위가 진다**(2026-08-30 지시로
   * 고른 「띠 안의 레일」).
   *
   * ⚠ **안 정했으면 회색이되 자리는 지킨다.** 「안 정한 것은 안 그린다」가
   * 이 프로젝트의 규칙이지만, 이 막대는 지우면 띠의 격자가 무너져 제목이
   * 왼쪽으로 밀린다 — 티켓마다 제목 시작점이 달라진다.
   */
  const priority = priorityOf(ticket.priority);
  const sig = priority ? `var(--st-${priority.tone}-f)` : 'var(--line-2)';
  /** 무엇이든 손댔나. **고정 바의 저장 단추가 이것으로 켜진다.** */
  const dirty = titleDirty || bodyDirty;

  /**
   * 고칠 곳을 두 번 누르면 열린다(2026-08-29 지시로 「수정」 단추를 걷었다).
   *
   * 권한이 없으면 아무 일도 안 한다 — 열어 놓고 저장이 403 으로 죽는 것보다
   * 처음부터 안 열리는 편이 낫다.
   */
  function openEditor() {
    if (canEdit && ticket) { setTextVersion(ticket.updated_at); setEditing(true); }
  }

  /** 제목과 설명을 **한 번에** 저장한다. 둘 중 바뀐 것만 보낸다. */
  async function saveText() {
    if (!ticket || !dirty || !title.trim()) return;
    const saved = await patch({
      ...(titleDirty ? { title: title.trim() } : {}),
      ...(bodyDirty ? { body } : {}),
    });
    if (saved) setEditing(false);
  }

  /**
   * 설명칸에 붙여넣은 파일을 올리고 **마크다운 조각을 커서 자리에 끼운다.**
   * 모양은 만들기 폼(`board/CreateForm.tsx`)과 같다 — 같은 일을 두 가지
   * 방식으로 하면 한쪽만 고쳐지는 자리가 된다.
   *
   * ⚠ **끼운 조각은 아직 안 저장됐다.** 여기서 바뀌는 것은 초안(`body`)이고,
   * 아래 고정 바의 「저장」을 눌러야 티켓에 남는다. 취소하면 올라간 파일만
   * 서버에 남는데, 그것을 되돌리는 길은 첨부 지우기뿐이다.
   */
  async function handleBodyPaste(event: ClipboardEvent<HTMLTextAreaElement>) {
    const files = imageFilesFromDataTransfer(event.clipboardData);
    // 글자 붙여넣기는 브라우저 기본 동작 그대로 둔다 — 여기서 막으면
    // 본문을 복사해 옮기는 일이 통째로 깨진다.
    if (files.length === 0) return;
    event.preventDefault();
    // 자리(커서)는 await 전에 잡는다 — 응답이 오는 사이 포커스가 떠날 수 있다.
    const box = event.currentTarget;
    const start = box.selectionStart ?? box.value.length;
    const end = box.selectionEnd ?? start;
    setPasting(true);
    setPasteError(null);
    try {
      const fragments: string[] = [];
      for (const file of files) fragments.push(await uploadAndInsert(file));
      const inserted = fragments.join('\n');
      setBody((prev) => {
        // 올리는 사이 글자가 바뀌었을 수 있다 — 자리를 범위 안으로 죈다.
        const from = Math.min(start, prev.length);
        const to = Math.min(Math.max(end, from), prev.length);
        return prev.slice(0, from) + inserted + prev.slice(to);
      });
    } catch (err) {
      if (err instanceof NotLoggedInError) {
        onUnauthorized();
        return;
      }
      setPasteError(errorMessage(err));
    } finally {
      setPasting(false);
    }
  }

  /**
   * 읽기 모드로 돌아간다. 안 저장한 제목·설명은 되돌린다.
   *
   * **버리기 전에 묻는다**(2026-10-18 UI 회의). 옛 「취소」는 이름 때문에
   * 이미 저장된 속성까지 되돌리는 것처럼 읽혔고, 쓰던 글은 묻지 않고 사라졌다.
   */
  function stopEditing() {
    // 위 분기가 보장하지만 클로저라 타입검사가 그것을 못 믿는다.
    if (!ticket) return;
    if (dirty && !window.confirm('저장하지 않은 제목·설명을 버릴까?')) return;
    setTitle(ticket.title);
    setBody(ticket.body);
    // 되돌린 글에 딸린 실패 문구도 함께 걷는다 — 남겨 두면 다음에 열었을
    // 때 지난 실패가 지금 것으로 읽힌다.
    setPasteError(null);
    setConflict(null);
    setEditing(false);
  }

  return (
    /*
      **세 층이다**(2026-08-30 지시: 머리와 아래 단추는 붙박이, 가운데만
      스크롤). 속성이 열넷이라 모달이 늘 스크롤되는데, 옛 판은 그때 제목이
      위로 밀려 **무슨 티켓을 보고 있었는지 사라졌다.**

      ⚠ **`min-h-0` 을 빼지 마라.** flex 자식의 기본 최소 높이가 콘텐츠
      크기라, 그것이 없으면 가운데 층이 안 줄고 모달이 통째로 늘어나 화면
      밖으로 나간다 — 스크롤이 아예 안 생긴다.
    */
    <div className="flex min-h-0 flex-1 flex-col">
      {/*
        **머리를 띠로 세운다**(2026-08-30 지시 — 시안 「띠 안의 레일」).
        옛 판은 흰 바탕에 회색 글자뿐이라 모달에서 가장 밋밋한 자리였다.

        왼쪽 6px 막대가 우선순위 색을 지고, **본문 인용선이 같은 굵기로
        같은 자리에 선다** — 눈이 두 조각을 한 선으로 잇는다. 그래서 둘의
        왼쪽 자리가 어긋나면 이 안은 뜻을 잃는다: 띠의 `pl-s6` 와 모달의
        `p-s6` 가 같은 값이라 맞는 것이고, 한쪽만 고치면 어긋난다.

        ⚠ **`-mx-s6 -mt-s6` 로 모달 패딩 밖까지 편다.** 안 그러면 띠가
        가운데 토막으로 떠 「머리」가 아니라 「상자 하나」로 읽힌다.

        ⚠ **띠를 가르는 것은 배경이 아니라 밑선이다.** `--sunk` 는 흰 판과
        **3%밖에 차이가 안 난다**(실측 #f3f6fa vs #ffffff) — 이 체계가
        「선만」이라 옅은 회색으로는 영역이 안 갈린다. 그래서 밑선을
        2px · `line-2` 로 세웠다. 1px 로 되돌리면 띠가 사라진다.
      */}
      <header className="grid shrink-0 grid-cols-[6px_1fr] items-stretch gap-s4 border-b-2 border-line-2 bg-sunk px-s6 py-s5 max-[720px]:gap-s3 max-[720px]:px-s4 max-[720px]:py-s4">
        <span
          className="rounded-full"
          style={{ background: sig }}
          aria-hidden="true"
        />
        <div>
        {/* ⚠ **오른쪽에 닫기 X 가 앉아 있다**(모달이 `absolute` 로 얹는다).
            `pr-s8` 로 그 자리를 비우지 않으면 오른쪽 끝의 글자가 X 밑에 깔린다. */}
        <div className="mb-s2 flex items-center gap-s3 pr-s8">
          <span className="tnum text-meta text-ink-3">{ticket.key}</span>
          {/* **주소는 여기서 나간다.** 번호 옆이 자리다 — 링크를 건네는
              것은 곧 이 번호를 건네는 것이라서다. 어느 화면에서 열렸든
              (보드·배포 보드·검색) 주소는 보드로 간다(`/tickets/:id`).
              실패를 조용히 안 넘긴다 — 「복사했다」로 읽고 빈 것을 붙여
              넣는 사고가 되므로, 막힌 자리에서는 주소를 직접 보여 준다. */}
          <button
            type="button"
            aria-label="티켓 링크 복사"
            title="링크 복사"
            onClick={() =>
              void (async () => {
                const url = ticketUrl(ticket.id);
                if (await copyText(url)) {
                  toast.success('티켓 링크를 복사했다');
                  return;
                }
                window.prompt('복사가 막혀 있다 — 직접 복사해라', url);
              })()
            }
            className="grid size-s5 shrink-0 place-items-center rounded-sm text-ink-3 transition-colors hover:bg-panel hover:text-ink max-[720px]:size-s8"
          >
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none" aria-hidden="true">
              <path
                d="M5.7 8.3l2.6-2.6M6.7 3.8l1.4-1.4a2.2 2.2 0 0 1 3.1 3.1L9.8 6.9M7.3 10.2l-1.4 1.4a2.2 2.2 0 0 1-3.1-3.1l1.4-1.4"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinecap="round"
              />
            </svg>
          </button>
          {/*
            **보류는 머리에 있다**(2026-09-02). 배포 배지와 **같은 자리·같은
            무게**다 — 둘 다 「이 티켓이 지금 어떤 상태인가」를 한눈에 말하는
            사실이고, 속성 묶음까지 내려가야 알 수 있으면 늦다.

            ⚠ **배포 배지보다 앞에 둔다.** 미뤄 둔 티켓은 배포 여부보다
            「지금 안 한다」가 먼저 읽혀야 한다.

            ⚠ **색을 violet 으로 골랐다.** red 는 「고장」, amber 는 이미 옆
            자리의 「칸에 담김」이 쓰고 있고, gray 는 옆의 「미배포」와 같아
            눈에 안 띈다 — 보류는 오류가 아니라 **결정**이다.

            사유는 배지 옆에 **줄여서** 붙는다(500자까지 들어온다). 전문은
            아래 속성의 「보류」 줄에서 고치고 읽는다.
          */}
          {ticket.held_at && (
            <>
              <span className={`${BADGE} font-bold`} style={toneVars('violet')}>
                보류
              </span>
              {ticket.hold_reason && (
                <span
                  className="min-w-0 truncate text-micro text-ink-3"
                  title={ticket.hold_reason}
                >
                  {ticket.hold_reason}
                </span>
              )}
            </>
          )}
          {saving && <span className="text-micro text-ink-3">저장 중…</span>}

          {/* **「수정」 단추를 걷었다**(2026-08-29 지시). 고칠 곳을 두 번
              누르면 그 자리가 열린다 — 읽으러 온 사람은 아무것도 안 눌러도
              되고, 고치러 온 사람은 고칠 곳을 바로 짚는다.

              편집 중이라는 것은 **아래 고정 바**가 말한다. 여기에 또 적으면
              같은 말이 두 곳에 선다. */}
          {/* ⚠ **이 한 줄이 유일한 안내다.** 모달의 `description` 은
              `sr-only` 라 눈에 안 보이고, 「수정」 단추도 없다 — 빼면
              고칠 수 있다는 것 자체를 모르는 사람이 생긴다.

              **안내가 곧 단추다**(2026-10-03). 두 번 누르기는 손가락과
              키보드에 길이 없다 — 넓은 화면은 글자 그대로 두되 눌리게 하고,
              좁은 화면은 「고치기」 단추로 세운다. 더블클릭은 그대로 산다. */}
          {canEdit && editing && (
            <span className="ml-auto shrink-0 text-micro text-ink-3">고치는 중</span>
          )}
          {/* 2026-10-18: 「두 번 눌러 고침」 글자 단추를 「고치기」 하나로 합쳤다
              (UI 회의 — 안내 글자가 곧 단추라 눌러도 되는지 안 읽혔다).
              더블클릭은 그대로 산다. */}
          {canEdit && !editing && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="ml-auto shrink-0 max-[720px]:min-h-[44px]"
              onClick={openEditor}
            >
              고치기
            </Button>
          )}
        </div>
        {conflict && <EditConflict current={`${conflict.title}\n${conflict.body}`} draft={`${title}\n${body}`}
          onContinue={() => { setTicket(conflict); setTextVersion(conflict.updated_at); setConflict(null); setEditing(true); }} />}
        {editing ? (
          <textarea
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            aria-label="제목"
            rows={2}
            className="w-full resize-none rounded-md border border-line bg-panel px-s3 py-s2 text-lead leading-tight break-keep text-ink outline-none focus-visible:border-brand"
          />
        ) : (
          <h2
            className="m-0 text-h1 leading-tight break-keep"
            onDoubleClick={openEditor}
            title={canEdit ? '두 번 눌러 고침' : undefined}
          >
            {ticket.title}
          </h2>
        )}
        {/* **핵심 속성 한 줄**(2026-10-18 UI 회의). 본문을 위로 올리면서 「지금
            어디에 있고 누가 언제까지」가 속성 묶음까지 내려가야 읽히게 됐다 —
            그 넷만 제목 밑에 다시 세운다. 안 정한 것은 안 그린다. */}
        <div className="mt-s3 flex flex-wrap items-center gap-s2">
          <span className={`${BADGE} bg-panel font-bold text-ink-2`}>{columnLabel(ticket.col)}</span>
          <PriorityMark priority={ticket.priority} />
          <DueMark due={ticket.due_date} />
          {ticket.assignee && (
            <span className="inline-flex items-center gap-s1 text-micro text-ink-2">
              <AssigneeMark user={ticket.assignee} size={16} />
              {ticket.assignee.display_name}
            </span>
          )}
        </div>
        </div>
      </header>

      {/*
        **가운데 층 — 여기만 스크롤한다.** 위 머리와 아래 단추는 제자리에
        붙어 있다.
      */}
      <div className="ticket-cols min-h-0 flex-1 overflow-y-auto">
      {/* **두 열이다**(2026-10-18 C 현황판형). 왼쪽은 사람이 읽는 것(본문·
          댓글), 오른쪽은 고르고 기록되는 것(속성·이력). 좁으면 위아래로
          쌓인다(`styles.css` 의 `.ticket-cols`). */}
      <div className="ticket-main flex min-w-0 flex-col gap-s4 px-s6 pt-s5 max-[720px]:px-s4">

      {/* **티켓 내용이 맨 위다**(2026-10-18 UI 회의). 옛 순서(정보 → 내용)는
          속성 열넷을 지나야 본문이 나와, 읽으러 연 사람이 매번 스크롤했다.
          핵심 속성 넷은 머리의 한 줄이 먼저 말한다.

          ⚠ **경계를 속성 묶음보다 무겁게 준다.** 속성 묶음 사이는 `--line`
          한 줄인데, 여기는 `--line-2` 에 위 여백이 두 배다. 같은 굵기로
          그으면 「사람이 쓴 글」이 「기계가 채운 값」과 같은 칸으로 읽힌다 —
          실제로 그래서 「구분이 안 된다」는 지적을 받았다.

          **이름표는 안 단다.** Jira·Notion·GitHub·Linear 넷 다 본문에는
          라벨을 안 붙인다 — 이름표는 이름·값 쌍(속성)의 것이고, 본문은
          자리와 생김새로 스스로를 말한다. */}
      {/*
        **인용선이 머리 막대의 아래 토막이다**(2026-08-30 지시).

        굵기(6px)와 왼쪽 자리가 띠의 막대와 같아서, 사이에 속성이 끼어
        있어도 눈이 둘을 한 선으로 잇는다. **셋 중 하나라도 어긋나면
        그냥 선 두 개가 된다** — 굵기 · 색 · 왼쪽 자리.

        경계선(`border-t-2`)을 걷었다. 인용선이 이미 「여기부터 다른
        것」을 말하는데 그 위에 가로선을 또 그으면 같은 말이 두 번이다.
      */}
      <section
        aria-label="티켓 내용"
        className="border-l-[6px] pl-s4"
        style={{ borderColor: sig }}
      >
        {/*
          ⚠ **소제목을 다시 들였다**(2026-08-30 지시). 2026-08-26 에는
          「화면에 소제목을 쓰지 마라」였는데, 그때는 속성 묶음 넷의 이름
          (「진행」·「분류」…)이 줄줄이 서서 화면이 설문지처럼 보였기
          때문이다.

          여기는 사정이 다르다. **사람이 쓴 글과 기계가 쓴 글이 같은 꼴로
          나란히 선다** — 인용선도 색도 같다. 그 둘을 가르는 것은 이 이름
          하나뿐이라, 빼면 어느 쪽이 누구 말인지 알 수 없다.
        */}
        <SectionTitle>티켓 내용</SectionTitle>
        {editing ? (
          <>
            <textarea
              value={body}
              onChange={(event) => setBody(event.target.value)}
              onPaste={(event) => {
                void handleBodyPaste(event);
              }}
              aria-label="설명"
              rows={5}
              placeholder="무슨 일인지 적는다"
              className="w-full resize-y rounded-md border border-line bg-panel px-s3 py-s2 text-body leading-body break-keep text-ink max-[720px]:text-lead"
            />
            {/* 붙여넣기가 된다는 것을 알리는 한 줄. 올리는 동안·실패도 이
                자리다 — 만들기 폼과 같은 문구를 쓴다. 형식 목록은 안 적는다:
                받는 것의 주인은 서버이고, 화면이 그 목록을 또 적으면 서버가
                늘렸을 때 두 벌이 어긋난다. */}
            <p
              className={`m-0 mt-s1 text-micro ${pasteError ? 'text-st-red-f' : 'text-ink-3'}`}
              role={pasteError ? 'alert' : undefined}
            >
              {pasting
                ? '스크린샷 올리는 중…'
                : (pasteError ?? '스크린샷은 여기에 붙여넣으면 첨부된다')}
            </p>
          </>
        ) : ticket.body ? (
          /* **마크다운으로 그린다**(2026-10-18). AI 초안이 절 제목·목록으로
             본문을 쓰는데 원문 그대로면 `##` 가 글자로 읽혔다. */
          <div
            onDoubleClick={openEditor}
            title={canEdit ? '두 번 눌러 고침' : undefined}
          >
            <Markdown>{ticket.body}</Markdown>
          </div>
        ) : (
          <p
            className="m-0 text-meta text-ink-3"
            onDoubleClick={openEditor}
            title={canEdit ? '두 번 눌러 고침' : undefined}
          >
            설명 없음
          </p>
        )}
      </section>


      {/*
        **바뀐 것**(2026-09-02). 창업 문장의 나머지 절반이다 — PR·커밋이
        「이 티켓이 어떤 코드가 됐나」라면, 이쪽은 **「이 티켓이 어떻게
        흘러왔나」**다. 그전에는 우선순위를 누가 언제 낮췄는지가 어디에도
        안 남고 마지막 값 하나만 남았다.

        **댓글 바로 앞이다.** 「무슨 일이 있었나」와 「무슨 말이 오갔나」가
        나란히 서야 한 화면에서 이어 읽힌다 — 그 사이에 무엇이 끼면 「이
        댓글이 저 변경을 두고 한 말」이라는 연결이 끊긴다.

        ⚠ **역사가 없으면 이 절은 아무것도 안 그린다.** 갓 만든 티켓마다
        빈 절이 서면 화면만 길어진다(`Activity` 머리말).
      */}
      {!editing && <BodyHistory ticketId={ticket.id} body={ticket.body} updatedAt={ticket.updated_at} canEdit={canEdit}
        onRestored={async () => {
          const fresh = await getTicket(ticket.id);
          setTicket(fresh); setTitle(fresh.title); setBody(fresh.body); onChanged?.(fresh);
        }} />}

      </div>
      <aside className="ticket-side flex min-w-0 flex-col gap-s4 px-s5 py-s5 max-[720px]:px-s4" aria-label="속성과 이력">
      {/*
        속성을 **묶음 넷으로 가른다**(2026-08-26 지시 — 「구분이 잘 안 된다」).
        요소가 열넷이 되면서 한 덩어리로는 눈이 어디서 쉬어야 할지 모른다.

        가르는 기준은 **사람이 그것을 언제 만지는가**다.
          진행  — 일하는 동안 계속 만진다
          분류  — 낼 때 정하고 거의 안 바꾼다
          발생  — 이슈에만 있고, 낼 때 적는다
          기록  — 서버가 적고 사람은 읽기만 한다

        고르는 것은 전부 라디오·체크박스다. 마감·소요 일수만 예외인데
        날짜와 숫자는 고를 것이 무한하다.
      */}
      <Group title="진행" onOpen={openEditor} hint={canEdit ? '두 번 눌러 고침' : undefined}>
        {/*
          **프로젝트를 옮길 수 있다**(2026-08-26 지시). ⚠ 옮기면 **라벨이
          비워진다** — 라벨은 프로젝트마다 따로 살아서, 그대로 두면 남의
          프로젝트 라벨을 단 티켓이 된다. 그 사실을 옆에 적어 둔다.
        */}
        <Row label="프로젝트">
          <EditOr
            editing={editing}
            read={projects.find((p) => p.id === ticket.project_id)?.name ?? null}
          >
            <span className="flex flex-wrap items-center gap-s2">
              <ChoiceGroup label="프로젝트">
                {projects.map((item) => (
                  <Radio
                    key={item.id}
                    name="ticket-project"
                    label={item.name}
                    nullable={false}
                    checked={ticket.project_id === item.id}
                    onChange={() => void patch({ project_id: item.id })}
                  >
                    {item.name}
                  </Radio>
                ))}
              </ChoiceGroup>
              {projects.length > 1 && (
                <span className="text-micro text-ink-3">옮기면 라벨 비워짐</span>
              )}
            </span>
          </EditOr>
        </Row>

        <Row label="상태">
          <EditOr editing={editing} read={columnLabel(ticket.col)}>
          <ChoiceGroup label="상태">
            {COLUMNS.map((item) => (
              <Radio
                key={item.id}
                name="ticket-col"
                label={item.label}
                nullable={false}
                checked={ticket.col === item.id}
                onChange={() => void move(item.id)}
              >
                {item.label}
              </Radio>
            ))}
          </ChoiceGroup>
          </EditOr>
        </Row>
        {/*
          **보류는 「상태」 바로 밑이다**(2026-09-02).

          ⚠ **칸과 다른 축이라는 것이 여기서 보여야 한다.** 나란히 두면
          「Todo 이면서 보류」가 한 눈에 읽히고, 보류가 다섯째 칸이 아니라는
          것도 함께 읽힌다 — 업무 칸 넷은 확정이라 늘리지 않았다.

          ⚠ **`EditOr` 로 안 감쌌다.** 그 껍데기는 라디오·날짜처럼 **고르는**
          것을 읽기 모드에서 감춰 오조작을 막는 장치인데, 보류는 고르는 값이
          아니라 누르는 일이다. 두 번 눌러야 나타나면 「안 할 요청을 미룰
          곳이 없다」는 문제가 그대로 남는다 — 소요 일수(`EstimateInput`)가
          같은 이유로 읽기 모드에 서 있다.

          **권한도 손잡이도 없으면 줄 자체를 안 그린다.** 값이 없는데 이름표만
          선 빈 줄이 되기 때문이다.
        */}
        {(canEdit || ticket.held_at) && (
          <Row label="보류">
            <HoldControl
              heldAt={ticket.held_at}
              reason={ticket.hold_reason}
              draft={holdReason}
              onDraft={setHoldReason}
              canEdit={canEdit}
              busy={holding}
              confirming={holdConfirm}
              onConfirm={setHoldConfirm}
              onHold={(reason) => void hold(true, reason)}
              onRelease={() => void hold(false)}
            />
          </Row>
        )}
        {(editing || ticket.assignee) && (
        <Row label="작업자">
          <EditOr editing={editing} read={ticket.assignee ? (
              <>
                <AssigneeMark user={ticket.assignee} size={18} />
                {ticket.assignee.display_name}
                <span className="ml-s1 text-micro text-ink-3">{ROLE_LABEL[ticket.assignee.role]}</span>
              </>
            ) : null}>
          <ChoiceGroup label="작업자">
            {members.map((item) => (
              <Radio
                key={item.id}
                name="ticket-assignee"
                label={item.display_name}
                checked={ticket.assignee?.id === item.id}
                onChange={(on) => void patch({ assignee_id: on ? item.id : null })}
              >
                <AssigneeMark user={item} size={18} />
                {item.display_name}
                <span className="ml-s1 text-micro text-ink-3">{ROLE_LABEL[item.role]}</span>
              </Radio>
            ))}
          </ChoiceGroup>
          </EditOr>
        </Row>
        )}
        {(editing || ticket.due_date) && (
        <Row label="마감">
          <EditOr editing={editing} read={ticket.due_date ? <span className="tnum">{ticket.due_date}</span> : null}>
          <input
            type="date"
            aria-label="마감일"
            value={ticket.due_date ?? ''}
            onChange={(event) => void patch({ due_date: event.target.value || null })}
            className="min-h-s7 rounded-md border border-line bg-panel px-s3 text-meta text-ink max-[720px]:min-h-[44px] max-[720px]:text-lead"
          />
          </EditOr>
        </Row>
        )}
        {(editing || ticket.started_at) && (
        <Row label="작업 일자">
          {ticket.started_at ? (
            <span className="inline-flex min-h-s7 items-center gap-s3 text-meta text-ink-2">
              <span className="tnum">
                {workSpan(ticket.started_at, ticket.estimate_days)}
              </span>
              {ticket.col === 'in_progress' && (
                <EstimateInput
                  value={ticket.estimate_days}
                  onSave={(days) => void move('in_progress', days)}
                />
              )}
            </span>
          ) : (
            <span className="inline-flex min-h-s7 items-center text-meta text-ink-3">
              「작업 중」으로 옮기면 기록됨
            </span>
          )}
        </Row>
        )}
        {(editing || ticket.finished_at) && (
        <Row label="완료 일자">
          <span className="inline-flex min-h-s7 items-center tnum text-meta text-ink-2">
            {ticket.finished_at ? (
              formatDay(ticket.finished_at)
            ) : (
              <span className="text-ink-3">「완료」로 옮기면 기록됨</span>
            )}
          </span>
        </Row>
        )}
      </Group>

      {(editing || ticket.type || ticket.priority || ticket.labels.length > 0) && (
      <Group title="분류" onOpen={openEditor} hint={canEdit ? '두 번 눌러 고침' : undefined}>
        {(editing || ticket.type) && (
        <Row label="작업 유형">
          <EditOr editing={editing} read={typeOf(ticket.type)?.label ?? null}>
          <ChoiceGroup label="타입">
            {TYPES.map((item) => (
              <Radio
                key={item.id}
                name="ticket-type"
                label={item.label}
                checked={ticket.type === item.id}
                onChange={(on) => void patch({ type: on ? item.id : null })}
              >
                {/* 배지가 곧 이름이다 — 글자를 또 적지 않는다. */}
                <TypeMark type={item.id} />
              </Radio>
            ))}
          </ChoiceGroup>
          </EditOr>
        </Row>
        )}
        {(editing || ticket.priority) && (
        <Row label="우선순위">
          <EditOr editing={editing} read={<PriorityMark priority={ticket.priority} />}>
          <ChoiceGroup label="우선순위">
            {PRIORITIES.map((item) => (
              <Radio
                key={item.id}
                name="ticket-priority"
                label={item.label}
                checked={ticket.priority === item.id}
                onChange={(on) => void patch({ priority: on ? item.id : null })}
              >
                <PriorityMark priority={item.id} />
              </Radio>
            ))}
          </ChoiceGroup>
          </EditOr>
        </Row>
        )}
        {(editing || ticket.labels.length > 0) && (
        <Row label="라벨">
          <EditOr editing={editing} read={ticket.labels.length ? (
              <span className="flex flex-wrap gap-s2">
                {ticket.labels.map((l) => (
                  <LabelChip key={l.id} label={l} />
                ))}
              </span>
            ) : null}>
          {labels.length === 0 ? (
            <span className="text-meta text-ink-3">라벨 없음</span>
          ) : (
            <ChoiceGroup label="라벨">
              {labels.map((label) => {
                const on = ticket.labels.some((item) => item.id === label.id);
                return (
                  <Check
                    key={label.id}
                    label={label.name}
                    checked={on}
                    onChange={(next) =>
                      void patch({
                        label_ids: next
                          ? [...ticket.labels.map((x) => x.id), label.id]
                          : ticket.labels.filter((x) => x.id !== label.id).map((x) => x.id),
                      })
                    }
                  >
                    <LabelChip label={label} muted={!on} />
                  </Check>
                );
              })}
            </ChoiceGroup>
          )}
          </EditOr>
        </Row>
        )}
      </Group>
      )}

      {/* 이슈에만 있다 — 할 일에는 「어디서 났나」가 없다. */}
      {ticket.kind === 'issue' && (editing || ticket.device || ticket.environment) && (
        <Group title="발생 환경" onOpen={openEditor} hint={canEdit ? '두 번 눌러 고침' : undefined}>
            {(editing || ticket.device) && (
            <Row label="디바이스">
          <EditOr editing={editing} read={deviceLabel(ticket.device)}>
              <ChoiceGroup label="디바이스">
                {DEVICES.map((item) => (
                  <Radio
                    key={item.id}
                    name="ticket-device"
                    label={item.label}
                    checked={ticket.device === item.id}
                    onChange={(on) => void patch({ device: on ? item.id : null })}
                  >
                    {item.label}
                  </Radio>
                ))}
              </ChoiceGroup>
          </EditOr>
            </Row>
            )}

            {(editing || ticket.environment) && (
            <Row label="환경">
          <EditOr editing={editing} read={environmentLabel(ticket.environment)}>
              <ChoiceGroup label="환경">
                {ENVIRONMENTS.map((item) => (
                  <Radio
                    key={item.id}
                    name="ticket-environment"
                    label={item.label}
                    checked={ticket.environment === item.id}
                    onChange={(on) => void patch({ environment: on ? item.id : null })}
                  >
                    {item.label}
                  </Radio>
                ))}
              </ChoiceGroup>
          </EditOr>
            </Row>
            )}
        </Group>
      )}

      <Group title="기록" onOpen={openEditor} hint={canEdit ? '두 번 눌러 고침' : undefined}>
        <Row label="유형">
          {/* **고칠 수 없다.** 만들 때 정해진 값이고, 이슈로 낸 것이 나중에
              할 일이 되는 것이 아니라 이슈를 보고 할 일을 따로 낸다. */}
          <span className="inline-flex min-h-s7 items-center text-meta text-ink-2">
            {kindLabel(ticket.kind)}
          </span>
        </Row>
        <Row label="생성자">
          <span className="inline-flex min-h-s7 items-center gap-s2 text-meta text-ink-2">
            {ticket.reporter ? (
              <>
                <AssigneeMark user={ticket.reporter} size={18} />
                {ticket.reporter.display_name}
              </>
            ) : (
              '알 수 없음'
            )}
          </span>
        </Row>
        <Row label="생성 일자">
          <span className="inline-flex min-h-s7 items-center tnum text-meta text-ink-2">
            {formatDay(ticket.created_at)}
          </span>
        </Row>
        <Row label="수정 일자">
          <span className="inline-flex min-h-s7 items-center tnum text-meta text-ink-2">
            {formatDay(ticket.updated_at)}
          </span>
        </Row>
      </Group>

      <Activity ticketId={ticket.id} rail={sig} onUnauthorized={onUnauthorized} />
      </aside>
      {/* 댓글은 **속성 뒤**에 선다(2026-10-19). 휴대폰에서는 한 줄로 쌓여서,
          옛 순서(본문·댓글·속성)로는 상태를 바꾸려면 댓글을 다 지나가야 했다.
          넓은 화면에서는 격자가 본문 아래로 끌어올린다(`.ticket-cols`). */}
      <div className="ticket-comments flex min-w-0 flex-col gap-s4 px-s6 pb-s5 max-[720px]:px-s4 max-[720px]:pt-s5">
      {/*
        **댓글**(2026-09-01 — 「댓글은 안 만든다」를 사용자가 뒤집었다).
        순서는 티켓 정보 → 티켓 내용 → AI 요약 → PR·커밋 → 바뀐 것 → 댓글 —
        대화는 내용을 읽은 다음의 일이라 맨 끝이다.

        위 두 절과 **같은 레일**(굵기·색·들여쓰기)을 쓴다 — 셋이 같은
        층위의 덩어리다. 권한으로 안 가린다: 이 모달이 열렸다는 것이 곧
        `ticket.view` 가 있다는 뜻이고, 서버가 그 키로 읽기·쓰기를 판정한다.
      */}
      <Comments
        ticketId={ticket.id}
        user={user}
        rail={sig}
        onUnauthorized={onUnauthorized}
      />
      </div>
      </div>

      {/*
        **아래 층 — 단추는 전부 여기 하나에 모인다**(2026-08-29 지시: 상세
        모달의 모든 단추는 우측 하단 고정).

        ⚠ **`sticky` 를 걷었다**(2026-08-30). 이제 층이 셋이라 이 바가
        스크롤과 무관하게 제자리에 있다 — `sticky` 는 스크롤 컨테이너
        안에서만 뜻이 있는데, 그 컨테이너가 위로 올라갔다.
      */}
      {(editing || (ticket.reporter?.id === user.id && can(user, 'ticket.delete'))) && (
        <div className="flex shrink-0 items-center justify-end gap-s2 border-t border-line bg-panel px-s6 py-s3">
          {/* ⚠ **지우기가 저장 옆에 선다.** 「모든 단추는 우측 하단」이라는
              2026-08-29 지시를 그대로 따른 결과다 — 파괴적 단추를 따로
              떼어 놓는 것이 보통이라, 오조작이 나면 이 자리를 먼저 의심해라.
              그래서 색을 죽이고(`ghost`·`ink-3`) 맨 왼쪽에 둔다.
              서버는 권한만 본다(`ticket.delete`); 「낸 사람」은 화면의
              조심이지 게이트가 아니다. */}
          {ticket.reporter?.id === user.id && can(user, 'ticket.delete') && (
            <Button
              variant="ghost"
              size="sm"
              className="text-ink-3"
              disabled={saving}
              onClick={() => void remove()}
            >
              지우기
            </Button>
          )}

          {editing && (
            <span className="flex items-center gap-s2">
              {/* 속성과 글의 저장 방식이 다르다는 것을 한 줄로 말한다 —
                  이것이 없어서 「저장을 눌러야 속성도 남나」를 물었다. */}
              <span className="mr-s2 text-micro text-ink-3 max-[720px]:hidden">
                속성은 고르는 즉시 저장된다
              </span>
              <Button variant="ghost" size="sm" onClick={stopEditing}>
                편집 끝내기
              </Button>
              {/*
                ⚠ **이 단추가 저장하는 것은 제목과 설명뿐이다.** 나머지
                속성은 고르는 즉시 저장된다 — 라디오를 누르는 순간이 곧
                결정이라 「저장을 눌러야 반영된다」로 만들면 오히려 헷갈린다.

                그래서 **손댄 것이 없으면 비활성**이다: 속성만 만지고 나가는
                사람에게 켜진 저장 단추를 보이면 「눌러야 하나」를 묻게 된다.
              */}
              <Button
                size="sm"
                disabled={!dirty || !title.trim() || saving}
                onClick={() => void saveText()}
              >
                {saving ? '저장 중…' : '제목·설명 저장'}
              </Button>
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * 속성 묶음 하나. **소제목과 가는 선으로 가른다** — 상자를 넷 두면 모달이
 * 액자 전시가 된다. 선 하나면 「여기부터 다른 이야기」가 충분히 전해진다.
 */
function Group({
  title,
  children,
  onOpen,
  hint,
}: {
  title: string;
  children: React.ReactNode;
  /** 두 번 누르면 편집이 열린다(2026-08-29 지시로 「수정」 단추를 걷었다). */
  onOpen?: () => void;
  hint?: string;
}) {
  return (
    /* **소제목을 안 쓴다**(2026-08-26 지시). 「진행」·「분류」 같은 이름표가
       줄줄이 서면 화면이 설문지처럼 보인다. **가르는 일은 선이 한다** —
       이름은 화면 낭독기만 듣는다(`aria-label`). */
    <section
      aria-label={title}
      onDoubleClick={onOpen}
      title={hint}
      className="border-t border-line pt-s4 first:border-t-0 first:pt-0"
    >
      {/*
        **넓으면 두 벌로 선다**(2026-08-30 지시). 티켓 속성이 열넷이라 한
        벌로 세우면 오른쪽 절반이 통째로 비고, 세로로 길어져 아래쪽을 보려면
        스크롤해야 했다.

        ⚠ **분기점이 720px 이다** — 이 앱이 좁은 화면을 가르는 그 값이고,
        그보다 좁으면 값 칸이 150px 밑으로 떨어져 「2026. 08. 26.」 같은
        값이 두 줄로 접힌다.

        홀수 개인 묶음은 마지막 하나가 왼쪽에 혼자 선다 — 그 자리를 빈
        칸으로 채우지 않는다. 없는 것을 그리는 것보다 낫다.
      */}
      {/* ⚠ **이름표 칸이 60px 인 것은 재서 정한 값이다**(2026-08-30).
          가장 긴 이름표가 「작업 유형」·「완료 일자」로 49px 인데 76px 을
          잡고 있었다 — 두 벌로 세우니 그 낭비가 두 배가 되어 우선순위
          라디오 넷이 12px 모자라 3+1 로 접혔다. 이름표를 줄이자 들어갔다.

          더 줄이지 마라. 이 값은 Pretendard 기준이고 Windows 맑은고딕은
          더 넓다 — 11px 이 그 여유다. */}
      <dl className="m-0 grid grid-cols-[64px_1fr] items-start gap-x-s3 gap-y-s3">
        {children}
      </dl>
    </section>
  );
}

/**
 * 속성 한 줄. `<dt>`/`<dd>` 를 쓰는 이유는 이것이 **이름과 값의 목록**이라서다 —
 * 읽는 쪽이 「칸: Todo」로 짝지어 읽는다.
 */
/* 절 이름(`SectionTitle`)은 `./section.tsx` 로 갔다(2026-09-01) — 댓글
   절이 같은 꼴을 써야 해서다. */

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      {/* 축 이름은 가장 조용하다. 사람이 읽는 것은 값이다. */}
      <dt className="pt-s2 text-meta text-ink-3">{label}</dt>
      <dd className="m-0 min-w-0">{children}</dd>
    </>
  );
}

/** 날짜를 `2026. 09. 02.` 로. **시각은 안 보여준다** — 티켓 일자는 날짜 단위다. */
function formatDay(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}. ${String(d.getMonth() + 1).padStart(2, '0')}. ${String(
    d.getDate(),
  ).padStart(2, '0')}.`;
}

/**
 * 소요 예정 일수를 적는다. **「작업 중」일 때만 보인다.**
 *
 * 저장 단추가 따로 있다 — 숫자를 치는 동안 한 자씩 보내면 「1일」을 거쳐
 * 「12일」이 되고, 그 사이 값이 그대로 기록된다.
 */
function EstimateInput({
  value,
  onSave,
}: {
  value: number | null;
  onSave: (days: number | null) => void;
}) {
  const [draft, setDraft] = useState(value === null ? '' : String(value));
  const parsed = draft.trim() === '' ? null : Number(draft);
  const valid = parsed === null || (Number.isInteger(parsed) && parsed > 0);
  const dirty = parsed !== value;

  return (
    <span className="inline-flex items-center gap-s2">
      <input
        type="number"
        min={1}
        aria-label="소요 예정 일수"
        value={draft}
        onChange={(event) => setDraft(event.target.value)}
        placeholder="며칠"
        className="min-h-s7 w-[72px] rounded-md border border-line bg-panel px-s2 text-meta text-ink max-[720px]:min-h-[44px] max-[720px]:text-lead"
      />
      {dirty && valid && (
        <Button size="sm" onClick={() => onSave(parsed)}>
          저장
        </Button>
      )}
    </span>
  );
}

/** 서버가 받는 사유의 상한(`HOLD_REASON_MAX`). 넘겨 보내면 422 다. */
const HOLD_REASON_MAX = 500;

/**
 * 보류 손잡이(2026-09-02 사용자 확정).
 *
 * **미루는 것과 사유를 적는 것을 두 걸음으로 갈랐다.** 사유를 먼저 물으면
 * 「무엇을 적어야 하나」가 문턱이 되어 미루는 것 자체를 안 하게 된다 —
 * 그러면 안 할 요청이 이슈 칸에 그대로 남고, 이 기능을 만든 이유가 사라진다.
 * 그래서 「미루기」는 한 번에 나가고 사유는 그 뒤에 덧붙인다.
 *
 * ⚠ **이름은 「보류」 하나다.** 「거절」·「중복」을 지어내지 마라 — 값 셋을
 * 담는 「결론」으로 물었는데 사용자가 보류 하나로 좁혔다.
 *
 * ⚠ **화면이 단추를 안 그리는 것은 조심이지 방어가 아니다.** 막는 것은
 * 서버(`require_perm("ticket.edit")`)다.
 */
function HoldControl({
  heldAt,
  reason,
  draft,
  onDraft,
  canEdit,
  busy,
  confirming,
  onConfirm,
  onHold,
  onRelease,
}: {
  heldAt: string | null;
  reason: string | null;
  draft: string;
  onDraft: (next: string) => void;
  canEdit: boolean;
  busy: boolean;
  confirming: boolean;
  onConfirm: (next: boolean) => void;
  /** 인자를 안 주면 「사유 없이 미룬다」다 — 서버가 시각만 찍는다. */
  onHold: (reason?: string | null) => void;
  onRelease: () => void;
}) {
  if (heldAt === null) {
    // 안 미룬 상태. 부르는 쪽이 `canEdit` 일 때만 이 줄을 그린다.
    return (
      <span className="inline-flex min-h-s7 flex-wrap items-center gap-s3">
        <span className="text-meta text-ink-3">안 미룸</span>
        <Button variant="outline" size="sm" disabled={busy} onClick={() => onHold()}>
          {busy ? '미루는 중…' : '미루기'}
        </Button>
      </span>
    );
  }

  if (!canEdit) {
    // 읽기만 한다. **머리의 배지는 사유를 줄여 보여 주므로** 전문은 여기다.
    return (
      <span className="inline-flex min-h-s7 flex-wrap items-center gap-s2 text-meta text-ink-2">
        <span className="tnum">{formatDay(heldAt)}부터</span>
        {reason && <span className="break-keep text-ink-3">{reason}</span>}
      </span>
    );
  }

  // 초안과 저장된 값이 다른가. 빈 문자열은 서버에서 `null` 이라 같은 것으로 본다.
  const dirty = (draft.trim() || null) !== reason;

  return (
    <span className="flex min-w-0 flex-col gap-s2">
      <span className="inline-flex min-h-s7 flex-wrap items-center gap-s3">
        <span className="tnum text-meta text-ink-2">{formatDay(heldAt)}부터</span>
        {confirming ? (
          /* 되돌리면 **서버가 사유도 함께 지운다.** 되돌릴 길이 없어서
             한 번 더 묻는다 — 프로젝트 지우기와 같은 꼴이다. */
          <>
            <span className="text-meta text-st-red-f break-keep">
              되돌리면 사유도 함께 지워진다
            </span>
            <Button variant="outline" size="sm" disabled={busy} onClick={onRelease}>
              {busy ? '되돌리는 중…' : '정말 되돌린다'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onConfirm(false)}>
              그만
            </Button>
          </>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="text-ink-3"
            disabled={busy}
            /* **사유가 없으면 안 묻는다.** 잃을 것이 없는데 한 번 더 물으면
               다음번에 그 물음을 안 읽고 누르게 된다. */
            onClick={() => (reason ? onConfirm(true) : onRelease())}
          >
            되돌리기
          </Button>
        )}
      </span>
      <span className="flex min-w-0 items-center gap-s2">
        <Input
          value={draft}
          onChange={(event) => onDraft(event.target.value)}
          maxLength={HOLD_REASON_MAX}
          aria-label="보류 사유"
          placeholder="왜 미루나 — 안 적어도 된다"
        />
        {/* 한 자씩 안 보낸다. 다시 미루는 것이라 **「언제 미뤘나」는 안 밀린다.** */}
        {dirty && (
          <Button size="sm" disabled={busy} onClick={() => onHold(draft.trim() || null)}>
            저장
          </Button>
        )}
      </span>
    </span>
  );
}

/**
 * 읽을 때와 고칠 때를 가른다.
 *
 * 읽기에서는 **값만** 보인다 — 라디오 상자가 늘어서 있으면 티켓을 읽으러 온
 * 사람이 무엇을 보는 화면인지 헷갈리고, 잘못 스치면 값이 바뀐다.
 */
function EditOr({
  editing,
  read,
  children,
}: {
  editing: boolean;
  read: React.ReactNode;
  children: React.ReactNode;
}) {
  if (editing) return <>{children}</>;
  return (
    <span className="inline-flex min-h-s7 items-center gap-s2 text-meta text-ink-2">
      {read ?? <span className="text-ink-3">비어 있음</span>}
    </span>
  );
}
